import { User } from "../user/user.model";
import { Client } from "../client/client.model";
import { Rate } from "./rate-type";
import { RateLocation } from "./rate-location/rate-location.type";
import { Carrier } from "../carrier/carrier.model";
import { RateMapper } from "./rate.mapper";
import { BaseLinkedRate } from "./rate-base-linked.model";
import { AirWeightBracketPrices } from "app/shared/air-weight-bracket/air-weight-bracket-prices.model";

export class AirRate extends BaseLinkedRate {
  constructor(
    protected id: number,
    protected name: string,
    protected discriminator: string,
    protected createdBy?: User,
    protected updatedBy?: User,
    protected createdAt?: Date,
    protected updatedAt?: Date,
    protected type?: string,
    protected source?: string,
    protected status?: string,
    protected internalNote?: string,
    protected externalNote?: string,
    protected pmNote?: string,
    protected comment?: string,
    protected deepLink?: string,
    protected carrier?: Carrier,
    protected validity?: Date[],
    protected currency?: string,
    protected weightTotals?: number,
    protected measurementTotals?: number,

    protected weightPriceLt45?: number,
    protected weightPriceGt45?: number,
    protected weightPriceGt100?: number,
    protected weightPriceGt300?: number,
    protected weightPriceGt500?: number,
    protected weightPriceGt1000?: number,

    protected totalWeightPriceLt45?: number,
    protected totalWeightPriceGt45?: number,
    protected totalWeightPriceGt100?: number,
    protected totalWeightPriceGt300?: number,
    protected totalWeightPriceGt500?: number,
    protected totalWeightPriceGt1000?: number,

    protected minPrice?: number,
    protected blPrice?: number,
    protected originLocation?: RateLocation,
    protected destinationLocation?: RateLocation,
    protected contractNumber?: string,
    protected restrictedClients?: Client[],
    protected restrictedUsers?: User[],
    protected rootParent?: Rate,
    protected incoterms: string[] = [],
    // TODO: We should consider a new model AirFreightRate for this
    protected chargeableWeight?: number,
    protected chargeableWeightPrice?: number,
    protected chargeableWeightMargin?: number,
    public availableOnline?: boolean,
    public weightBracketMargins?: AirWeightBracketPrices,
    public maxWeightPerShipment?: number,
    public maxWeightPerPiece?: number,
    public maxMeasurementPerShipment?: number,
    public maxMeasurementPerPiece?: number,
    public maxTaxWeight?: number,
    public maxLength?: number,
    public maxHeight?: number,
    public maxWidth?: number,
    public maxLWH?: number,
    public marginType?: string,
  ) {
    super(
      id,
      name,
      discriminator,
      createdBy,
      updatedBy,
      createdAt,
      updatedAt,
      type,
      source,
      status,
      internalNote,
      externalNote,
      pmNote,
      comment,
      deepLink,
      carrier,
      validity,
      currency,
      originLocation,
      destinationLocation,
      contractNumber,
      restrictedClients,
      restrictedUsers,
      rootParent,
    );
  }

  public getWeightTotals(): number {
    return this.weightTotals;
  }

  public setWeightTotals(param: number) {
    this.weightTotals = param;
  }

  public getMeasurementTotals(): number {
    return this.measurementTotals;
  }
  public setMeasurementTotals(param: number) {
    this.measurementTotals = param;
  }

  // weight price methods

  public getWeightPriceLt45(): number {
    return this.weightPriceLt45;
  }
  public setWeightPriceLt45(param: number): void {
    this.weightPriceLt45 = param;
  }

  public getWeightPriceGt45(): number {
    return this.weightPriceGt45;
  }
  public setWeightPriceGt45(param: number): void {
    this.weightPriceGt45 = param;
  }

  public getWeightPriceGt100(): number {
    return this.weightPriceGt100;
  }
  public setWeightPriceGt100(param: number): void {
    this.weightPriceGt100 = param;
  }

  public getWeightPriceGt300(): number {
    return this.weightPriceGt300;
  }
  public setWeightPriceGt300(param: number): void {
    this.weightPriceGt300 = param;
  }

  public getWeightPriceGt500(): number {
    return this.weightPriceGt500;
  }
  public setWeightPriceGt500(param: number): void {
    this.weightPriceGt500 = param;
  }

  public getWeightPriceGt1000(): number {
    return this.weightPriceGt1000;
  }
  public setWeightPriceGt1000(param: number): void {
    this.weightPriceGt1000 = param;
  }

  // total weight price methods

  public getTotalWeightPriceLt45(): number {
    return this.totalWeightPriceLt45;
  }
  public setTotalWeightPriceLt45(param: number): void {
    this.totalWeightPriceLt45 = param;
  }

  public getTotalWeightPriceGt45(): number {
    return this.totalWeightPriceGt45;
  }
  public setTotalWeightPriceGt45(param: number): void {
    this.totalWeightPriceGt45 = param;
  }

  public getTotalWeightPriceGt100(): number {
    return this.totalWeightPriceGt100;
  }
  public setTotalWeightPriceGt100(param: number): void {
    this.totalWeightPriceGt100 = param;
  }

  public getTotalWeightPriceGt300(): number {
    return this.totalWeightPriceGt300;
  }
  public setTotalWeightPriceGt300(param: number): void {
    this.totalWeightPriceGt300 = param;
  }

  public getTotalWeightPriceGt500(): number {
    return this.totalWeightPriceGt500;
  }
  public setTotalWeightPriceGt500(param: number): void {
    this.totalWeightPriceGt500 = param;
  }

  public getTotalWeightPriceGt1000(): number {
    return this.totalWeightPriceGt1000;
  }
  public setTotalWeightPriceGt1000(param: number): void {
    this.totalWeightPriceGt1000 = param;
  }

  public getMinPrice(): number {
    return this.minPrice;
  }

  public setMinPrice(param: number) {
    this.minPrice = param;
  }

  public getBlPrice(): number {
    return this.blPrice || 0;
  }

  public setBlPrice(param: number) {
    this.blPrice = param;
  }

  public getChargeableWeight(): number {
    return this.chargeableWeight;
  }
  public setChargeableWeight(value: number) {
    this.chargeableWeight = value;
  }

  public getChargeableWeightPrice(): number {
    return this.chargeableWeightPrice;
  }
  public setChargeableWeightPrice(value: number) {
    this.chargeableWeightPrice = value;
  }

  public getChargeableWeightMargin(): number {
    return this.chargeableWeightMargin;
  }

  public getAvailableOnline(): boolean {
    return this.availableOnline;
  }

  public getIncoterms(): string[] {
    return this.incoterms;
  }

  public setIncoterms(incoterms: string[]): void {
    this.incoterms = incoterms;
  }

  public getClone(): Rate {
    return RateMapper.map({
      id: this.id,
      name: this.name,
      discriminator: this.discriminator,
      createdBy: this.createdBy,
      updatedBy: this.updatedBy,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt,
      type: this.type,
      status: this.status,
      internalNote: this.internalNote,
      externalNote: this.externalNote,
      carrier: this.carrier,
      validity: this.validity,
      currency: this.currency,
      weightTotals: this.weightTotals,
      measurementPriceTotals: this.measurementTotals,
      weightPriceLt45: this.weightPriceLt45,
      weightPriceGt45: this.weightPriceGt45,
      weightPriceGt100: this.weightPriceGt100,
      weightPriceGt300: this.weightPriceGt300,
      weightPriceGt500: this.weightPriceGt500,
      weightPriceGt1000: this.weightPriceGt1000,
      blPrice: this.blPrice,
      originLocation: this.originLocation,
      destinationLocation: this.destinationLocation,
      contractNumber: this.contractNumber,
      restrictedClients: this.restrictedClients,
      restrictedUsers: this.restrictedUsers,
      rootParent: this.rootParent,
      chargeableWeight: this.chargeableWeight,
      chargeableWeightPrice: this.chargeableWeightPrice,
    });
  }
}
